import React, { useMemo, useState } from 'react';
import { sanitize } from 'helpers/utils/htmlSanitize';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import Image from 'frontastic/lib/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { mediumDesktop, tablet } from 'helpers/utils/screensizes';
import WhiteOverlay from 'components/commercetools-ui/atoms/overlay/white-overlay';
import usePreloadImages from 'helpers/hooks/usePreloadImages';

import { GenericSectionContent, MediaCard, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import styles from './index.module.css';

export default function HeroBannerRendering({ content }: { content: GenericSectionContent }) {
  const { cards, additional_css, additional_css_class_names } = content;

  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'HeroBannerRendering');

  const { isTouchDevice } = useTouchDevice();

  const [isDesktopSize] = useMediaQuery(mediumDesktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const [loading, setLoading] = useState(true);
  const showArrows = useMemo(
    () =>
      !isTouchDevice &&
      !((isDesktopSize && cards?.length <= 4) || (!isDesktopSize && cards?.length <= 2)),
    [isDesktopSize, isTouchDevice, cards?.length],
  );

  usePreloadImages(
    [cards?.[0]?.desktop_image?.url, cards?.[1]?.desktop_image?.url].filter(
      (url): url is string => !!url,
    ),
  );

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      {loading && <WhiteOverlay />}
      <div className={`hero-slider-container relative w-full pt-0 pb-24 lg:pb-40 2xl:pb-60 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <Slider
          containerClassName={showArrows ? '' : ''}
          slidesPerView={1}
          dots={true}
          arrows={isTabletSize}
          innerArrows
          allowArrowsOnTouchDevice
          allowTouchMove={true}
          prevButtonStyles={{ padding: '20px', left: '0px' }}
          nextButtonStyles={{ padding: '20px', right: '0px' }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
        >
          {cards?.map(
            (slide: MediaCard, index: number) => (
              <div key={index}>
                <div className="relative h-[18rem] md:h-[34rem] 2xl:h-[45rem]">
                  <picture>
                    <source media="(max-width:699px)" srcSet={`${slide?.mobile_image?.url ?? slide?.desktop_image?.url}`} />
                    <source media="(max-width:640px)" srcSet={`${slide?.mobile_image?.url ?? slide?.desktop_image?.url}`} />
                    <Image
                      priority
                      loading="eager"
                      src={slide?.desktop_image?.url}
                      alt={slide?.desktop_image?.description ?? ''}
                      fill
                      style={{ objectFit: 'cover' }}
                      className="brightness-75"
                      onLoad={() => setLoading(false)}
                      height={500}
                      width={500}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    />
                  </picture>
                </div>
                {isTabletSize ? (
                  // Full-width overlay so the heading gets the whole banner to lay out in. A
                  // shrink-to-fit box with a `w-1/4` heading wrapper broke a title like
                  // "Caroma Riviere Collection" over three lines; the V1 hero this replaces put
                  // the h1 in a full-width container and kept it on one.
                  <div className="absolute left-0 right-0 top-1/2 w-full -translate-y-1/2 pl-128 text-left">
                    <div>
                      <Typography
                        as="h1"
                        className="text-left text-34 font-semibold text-[#FFF] drop-shadow-sm md:leading-[3rem] xl:text-48 xl:leading-[4.375rem] 2xl:text-62"
                      >
                        {slide?.title}
                      </Typography>
                    </div>
                    <div className="w-[60%]">
                      <div
                        className="mt-22 text-left text-16 font-medium leading-8 tracking-wide text-white xl:text-16  2xl:text-20"
                        dangerouslySetInnerHTML={{ __html: sanitize(slide?.description ?? '') }}
                      />
                    </div>
                    {slide?.ctas &&
                      slide?.ctas.map((cta: Cta) => (
                        <Link link={cta?.url} target={cta?.target} key={cta?.label}>
                          {/* White button / dark label. V2 dropped the CMS colour pickers the V1
                              hero read its button colours from, and every hero banner in the CMS
                              set them to exactly this pair. */}
                          <Button
                            className="mt-22 rounded-none border border-white bg-white shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:mt-36 md:px-32 md:py-18 lg:mt-32"
                          >
                            <Typography as="span" className="text-12 text-[#090909] md:text-14 lg:text-16">
                              {cta?.label}
                            </Typography>
                          </Button>
                        </Link>
                      ))}
                  </div>
                ) : (
                  <div className="bg-black px-24 pb-63 pt-40">
                    <Typography as="h1" className="text-left text-34 font-semibold leading-tight text-white">
                      {slide?.title}
                    </Typography>
                    <div
                      className="mt-22 text-left text-16 text-white md:text-20 "
                      dangerouslySetInnerHTML={{ __html: sanitize(slide?.description ?? '') }}
                    />
                    {slide?.ctas &&
                      slide?.ctas.map((cta: Cta) => (
                        <Link link={cta?.url} target={cta?.target} key={cta?.label}>
                          <Button
                            className="mt-22 w-full rounded-none border border-white bg-white py-18 shadow-sm hover:!bg-[#F4F4F4]"
                          >
                            <Typography as="span" className="text-16 font-medium leading-6 text-[#090909]">
                              {cta?.label}
                            </Typography>
                          </Button>
                        </Link>
                      ))}
                  </div>
                )}
              </div>
            ),
          )}
        </Slider>
      </div>
    </>
  );
}
